// @flow

import type {
  Location,
  Navigation,
  NavigationNode,
  NavigationProduct,
  NavigationTopic,
} from 'docs/data';

export function getNavItems(navigation: Navigation): Array<NavigationProduct> {
  const activeTopic = navigation.topics.find((t) => t.active) || {};

  return (
    navigation.products.filter((p) => p.topic?.href === activeTopic.href) || []
  );
}

// This function takes the (static) navigation tree that's passed to the client
// as part of the initial state and determines which parts of the tree are "active"
// and "selected" based on the current location.
export const activateNavigation = (
  location: Location,
  navigation: Navigation,
): Navigation => {
  const isNodeActiveOrSelected = (node: NavigationNode) =>
    node.type === 'link' && (node.selected || node.active);

  const isTopicActive = (
    input: NavigationTopic,
    pathname: string,
    products: Array<NavigationProduct>,
  ): boolean => {
    if (input.href === location.pathname) {
      // Then the current location is the topic overview page
      return true;
    }
    if (input.sections) {
      // Then the current location is a nested product (only in legacy-split nav)
      const activeProduct = products.find((product) => product.active);
      return !!(activeProduct && input.sections.includes(activeProduct.text));
    }
    return false;
  };
  const activateTopic = (products: Array<NavigationProduct>) => (
    input: NavigationTopic,
  ): NavigationTopic => {
    return {
      ...input,
      active: isTopicActive(input, location.pathname, products),
    };
  };

  const activateNode = (input: NavigationNode): NavigationNode => {
    // If the node is a heading, it doesn't have children and doesn't need activation.
    if (input.type === 'heading') {
      return {...input};
    }

    // The node is selected if its URL matches the current location.
    const selected = input.href === location.pathname;

    // Recursively activate the children of the node, if any.
    const children = input.children.map(activateNode);

    let active = selected;
    let expanded = selected;

    // If a child of this node is active or selected, this node must also be active and expanded.
    if (children.some(isNodeActiveOrSelected)) {
      active = true;
      expanded = true;
    }

    return {
      ...input,
      children,
      selected,
      active,
      expanded,
    };
  };

  const activateProduct = (input: NavigationProduct): NavigationProduct => {
    const children = input.children.map(activateNode);
    return {
      ...input,
      children,
      active: children.some(isNodeActiveOrSelected),
    };
  };

  const products = navigation.products.map(activateProduct);
  const topics = navigation.topics.map(activateTopic(products));

  return {
    version: navigation.version,
    topics,
    products,
  };
};
